import React, { CSSProperties } from 'react';
import classnames from 'classnames';
import AntdButton from 'antd/lib/button';
import {extractDomProps} from '../../../utils/DomPropsExtractor';
import { convertSize } from '../../../utils/StyleUtils';
import { getEnvironment } from '@unifocus/js-utils';

export declare type ButtonType = "default" | "primary" | "ghost" | "dashed" | "link" | "text";
export declare type ButtonShape = "circle" | "round";
export declare type ButtonSizeType = 'small' | 'middle' | 'large' | undefined;

/**
 * {@docCategory Button}
 */
export interface ButtonProps {
   /**
    * Can be set to primary ghost dashed link text default
    * @defaultvalue 'default'
    */
   type?: ButtonType;

   /**
    * Set the icon component of button
    */
   icon?: React.ReactNode;

   /**
    * Can be set button shape
    */
   shape?: ButtonShape;

   /**
    * Set the size of button
    * @defaultvalue 'middle' unless mobile environment is set, in which case it is 'large'
    */
   size?: ButtonSizeType;

   /**
    * Make background transparent and invert text and border colors
    */
   ghost?: boolean;

   /**
    * Set the danger status of button
    */
   danger?: boolean;

   /**
    * Option to fit button width to its parent width
    */
   block?: boolean;

   /**
    * Set the loading status of button
    */
   loading?: boolean | {
      delay?: number;
   };

   /**
    * Redirect url of link button
    */
   href?: string;

   /**
    * Same as target attribute of a, works when href is specified
    */
   target?: string;

   /**
    * Disabled state of button
    */
   disabled?: boolean;

   /**
    * Set the handler to handle click event
    */
   onClick?: React.MouseEventHandler<HTMLElement>;

   /**
    * Set the handler to handle mouse down event
    */
   onMouseDown?: React.MouseEventHandler<HTMLElement>;

   /**
    * Set the handler to handle mouse up event
    */
   onMouseUp?: React.MouseEventHandler<HTMLElement>;

   /**
    * Set the handler to handle key up event
    */
   onKeyUp?: React.KeyboardEventHandler<HTMLElement>;

   /**
    * Set the handler to handle key down event
    */
   onKeyDown?: React.KeyboardEventHandler<HTMLElement>;

   /**
    * Set the handler to handle key press event
    */
   onKeyPress?: React.KeyboardEventHandler<HTMLElement>;

   /**
    * The width of the button. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    */
   width?: string | number;

   /**
    * This attribute lets you associate the Button to Forms anywhere in the document, not just inside a Form
    */
   form?: string;

   /**
    * When in a Form, does this button function as a submit button, a reset button, or just a
    * plain button.
    *
    * @defaultvalue 'button'
    */
   formButtonType?: 'submit' | 'reset' | 'button';

   /**
    * Whether or not to auto request focus
    */
   autoFocus?: boolean;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;
}

export const Button = (props: React.PropsWithChildren<ButtonProps>) => {

   const style: CSSProperties = {
      width: convertSize(props.width),
      ...props.style
   };

   const environment = getEnvironment() || {};
   const mobileClassName = (environment.mobile) ? 'uf-button-mobile' : undefined;
   const className = classnames('uf-button', mobileClassName, props.className);

   const size = props.size ? props.size : (environment.mobile ? 'large' : undefined);

   return (
      <AntdButton
         {...extractDomProps(props)}
         type={props.type}
         icon={props.icon}
         shape={props.shape}
         size={size}
         ghost={props.ghost}
         danger={props.danger}
         block={props.block}
         loading={props.loading}
         href={props.href}
         target={props.target}
         disabled={props.disabled}
         form={props.form}
         htmlType={props.formButtonType}
         autoFocus={props.autoFocus}
         onClick={props.onClick}
         onMouseDown={props.onMouseDown}
         onMouseUp={props.onMouseUp}
         onKeyUp={props.onKeyUp}
         onKeyDown={props.onKeyDown}
         onKeyPress={props.onKeyPress}
         className={className}
         style={style}
      >
         {props.children}
      </AntdButton>
   );
};
